import { Box, Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";

import {
  CreateTollFreeRegistrationMutationVariables,
  GetRegistrationQuery,
  Update10DlcBrandAndCampaignRegistrationMutationVariables,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { useCaptcha } from "~frontend/providers/CaptchaProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { InternalRegistration } from "~frontend/routes/subaccounts/phoneNumberList/TenDlc/InternalRegistration";
import { InternalTollFreeRegistrationForm } from "~frontend/routes/subaccounts/phoneNumberList/TollFreeRegistration";
import { palette } from "~frontend/theme";

import logo from "../../../components/icons/NavyLogo.svg";

const SubmittedRegistration = ({ children }: { children: ReactNode }) => (
  <PageContainer>
    <AccentBox sx={{ px: 10, py: 5 }}>
      <FlexBox direction="column" gap={1}>
        <Typography variant="h1Bold" component="h1">
          Thank you for registering!
        </Typography>

        {children}
        <Typography variant="body1" component="div">
          If you have any questions, please reach out to us at{" "}
          <a href="mailto:compliance@scaletowin.com">
            compliance@scaletowin.com
          </a>
          .
        </Typography>
      </FlexBox>
    </AccentBox>
  </PageContainer>
);

export const PublicComplianceRegistration = (): ReactNode => {
  const snackbar = useSnackbar();
  document.body.style.height = "100%";
  document.body.style.backgroundColor = palette.lightestGray;

  const { registrationType } = useParams<{
    registrationType: "ten-dlc" | "toll-free";
  }>();

  const [{ token, slug }] = useQueryParams({
    token: StringParam,
    slug: StringParam,
  });

  const intl = useIntl();

  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [registration, setRegistration] = useState<
    GetRegistrationQuery["getRegistration"] | null
  >(null);

  const captcha = useCaptcha();

  const queryString = `token=${token}&slug=${slug}&type=${registrationType}`;

  const getAxiosClient = useCallback(async () => {
    const client = axios.create({
      baseURL: "/public/registrations/",
    });

    client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 405) {
          console.warn("Got 405, retrying with new token");
          return new Promise((resolve, reject) => {
            captcha.showCaptcha(newToken => {
              client.defaults.headers["x-aws-waf-token"] = newToken;
              client.request(error.config).then(resolve).catch(reject);
            });
          });
        }
        return Promise.reject(error);
      }
    );
    return client;
  }, [captcha]);

  const fetchRegistration = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);

    try {
      const axiosClient = await getAxiosClient();
      const response = await axiosClient.get(`/status?${queryString}`);

      if (response.status !== 200) {
        throw new Error("Status check failed");
      }

      setRegistration(response.data);
    } catch (e) {
      console.error("Load status error", e);
      setStatusError(
        intl.formatMessage({
          defaultMessage:
            "There was an error getting your registration form. Please contact us at compliance@scaletowin.com",
          description:
            "Error message when compliance registration token is invalid",
        })
      );
    }

    setStatusLoading(false);
  }, [getAxiosClient, intl, queryString]);

  useEffect(() => {
    (async () => {
      await fetchRegistration();
    })().catch(console.error);
  }, [
    fetchRegistration,
    getAxiosClient,
    intl,
    queryString,
    registrationType,
    slug,
    token,
  ]);

  // No saving as draft for public links; this will submit directly to staff
  const handleSave = async (
    input: Omit<
      | Update10DlcBrandAndCampaignRegistrationMutationVariables["input"]
      | CreateTollFreeRegistrationMutationVariables["input"],
      "subaccountId"
    >
  ): Promise<{ errors: Error[] | string[] | undefined | null }> => {
    const axiosClient = await getAxiosClient();
    try {
      const response = await axiosClient.post(`/submit?${queryString}`, input);

      if (response.status !== 200) {
        return { errors: [response.data] };
      }

      setRegistration(response.data);
    } catch {
      snackbar.enqueueSnackbar({
        message: "Error submitting registration",
        variant: "error",
      });
      return { errors: [new Error("Error submitting registration")] };
    }

    return { errors: null };
  };

  if (!statusLoading && statusError) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <ErrorCard skipApollo errors={[statusError]} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: palette.lightestGray,
      }}
    >
      <PageContainer sx={{ mb: 0 }}>
        <img src={logo} alt="Scale to Win Logo" style={{ width: "70px" }} />
      </PageContainer>
      <AsyncWrapper
        data={registration}
        errors={statusError}
        loading={statusLoading}
      >
        {registration => {
          if (registrationType === "ten-dlc") {
            if (registration?.submittedAt) {
              return (
                <SubmittedRegistration>
                  <Typography variant="body1" component="div">
                    Our team will begin processing your 10DLC registration
                    imminently and reach out with any flags or questions. Please
                    note that 10DLC registrations can take{" "}
                    <strong>7-10 business days</strong> for the vetting body to
                    approve. Once the registration is complete, the primary
                    contact will receive an automated confirmation.
                  </Typography>
                </SubmittedRegistration>
              );
            }
            return (
              <InternalRegistration
                registration={registration!}
                handleSave={handleSave}
                disableAutosave
              />
            );
          } else if (registrationType === "toll-free") {
            if (registration?.createdAt) {
              return (
                <SubmittedRegistration>
                  <Typography variant="body1" component="div">
                    Our team will begin processing your toll-free registration
                    imminently and reach out with any flags or questions. Please
                    note that toll-free registrations can take{" "}
                    <strong>3-5 business days</strong> for the vetting body to
                    approve. Once the registration is complete, an automated
                    confirmation will be sent.
                  </Typography>
                </SubmittedRegistration>
              );
            }

            return (
              <InternalTollFreeRegistrationForm
                handleSubmitToStaff={handleSave}
              />
            );
          } else {
            return <ErrorCard errors={["This page is not found"]} />;
          }
        }}
      </AsyncWrapper>
    </Box>
  );
};
